import { useCallback, useEffect, useRef } from 'react'
import { dateTime, Frequency, recurences, RRule } from '@guestgoodie/helpers'
import { GraphQL } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon } from '@hugeicons/core-solid-rounded'

import RecurrenceSelect from './RecurrenceSelect'

export interface MultipleRecurrenceSelectProps {
  recurrences: GraphQL.RecurrenceFragmentFragment[]
  hasDuration?: boolean
  generalDuration?: number | null
  readOnly?: boolean
  name?: string
  invalid?: boolean
  onChange: (rrules: GraphQL.RecurrenceFragmentFragment[]) => void
}

// By default we assume 60 minutes
function newRecurrence(
  duration: number = 60 * 60_000
): GraphQL.RecurrenceFragmentFragment {
  const defaultRule = new RRule({
    freq: Frequency.DAILY,
    interval: 1,
    byhour: 0,
    byminute: 0
  })

  return {
    duration,
    startDate: dateTime.hotelDateNow().toString(),
    rrule: recurences.rruleToApiReadyString(defaultRule)
  }
}

export default function MultipleRecurrenceSelect({
  recurrences,
  generalDuration,
  hasDuration,
  readOnly,
  onChange,
  name,
  invalid = false
}: MultipleRecurrenceSelectProps) {
  const lastDuration = useRef<number | null | undefined>(generalDuration)

  const onUpdateRecurrences = useCallback(
    (index: number, recurrence: GraphQL.RecurrenceFragmentFragment) => {
      const newRRules =
        index >= recurrences.length
          ? [...recurrences, recurrence]
          : recurrences.map(function (r, i) {
              if (index === i) return recurrence
              return r
            })

      onChange(newRRules)
    },
    [recurrences, onChange]
  )

  const onDuplicate = useCallback(
    (index: number) => {
      const rec = recurrences[index]
      const newRecurrence = { ...rec }

      onUpdateRecurrences(recurrences.length, newRecurrence)
    },
    [recurrences, onUpdateRecurrences]
  )

  const onRemoveRecurrence = useCallback(
    (index: number) => {
      onChange(recurrences.toSpliced(index, 1))
    },
    [recurrences, onChange]
  )

  useEffect(() => {
    if (generalDuration == null) return
    if (lastDuration.current === generalDuration) return

    lastDuration.current = generalDuration

    const newRecs = recurrences.map(rec => {
      return {
        ...rec,
        duration: generalDuration
      }
    })

    onChange(newRecs)
  }, [generalDuration, recurrences, onChange])

  return (
    <div className="w-full overflow-x-auto py-2 px-1">
      <div className="flex flex-row items-start justify-start gap-2 min-h-20">
        {!readOnly && (
          <button
            type="button"
            tabIndex={0}
            onClick={() =>
              onUpdateRecurrences(
                recurrences.length,
                newRecurrence(generalDuration ?? undefined)
              )
            }
            aria-label="Wiederholung hinzufügen"
            name={name}
            aria-invalid={invalid}
            className="self-stretch border border-dashed rounded-small text-text-light border-text-light flex flex-row justify-center items-center shrink-0 w-10"
          >
            <HugeiconsIcon icon={PlusSignIcon} className="size-5" />
          </button>
        )}
        {recurrences.map(function (recurrence, index) {
          return (
            <RecurrenceSelect
              key={index}
              recurrence={recurrence}
              hasDuration={hasDuration}
              readOnly={readOnly}
              onChange={recurrence => onUpdateRecurrences(index, recurrence)}
              onDuplicate={readOnly ? undefined : () => onDuplicate(index)}
              onDelete={readOnly ? undefined : () => onRemoveRecurrence(index)}
            />
          )
        })}
      </div>
    </div>
  )
}
